import { Button } from "@/components/ui/button";
import { UnsavedChangesGuard } from "@/components/form/form";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import {
  organizationFormContainerClassName,
  organizationFormGridClassName,
} from "@/components/organization-forms/organization-form-layout";
import { PortalPageHeader } from "@/components/portal-page-header";
import { ensureRouteQueryData } from "@/lib/route-loader";
import {
  getParentOrganizationFormsGetFormQueryKey,
  getParentOrganizationFormsGetFormQueryOptions,
  useParentOrganizationFormsGetForm,
  useParentOrganizationFormsSaveResponse,
  useParentOrganizationFormsStartResponse,
  useParentOrganizationFormsSubmitResponse,
} from "@/orval/parent-organization-forms";
import { OrganizationFormFieldType, OrganizationFormResponseStatus } from "@/orval/model";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { SaveIcon, SendIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { OrganizationFormResponseField as ResponseField } from "@/components/organization-forms/organization-form-response-field";
import { numericIdParamSchema } from "@/lib/route-params";
import { Skeleton } from "@/components/ui/skeleton";
import { z } from "zod";

function ParentFormDetailPage() {
  const { organizationId, formId } = Route.useParams();
  const queryClient = useQueryClient();
  const { data, isLoading } = useParentOrganizationFormsGetForm(organizationId, formId);
  const [validationMessage, setValidationMessage] = useState<string>();
  const {
    watch,
    reset,
    setValue,
    formState: { isDirty },
  } = useForm({ defaultValues: { answers: {} as Record<number, string | null> } });
  const answers = watch("answers");
  const start = useParentOrganizationFormsStartResponse();
  const save = useParentOrganizationFormsSaveResponse();
  const submit = useParentOrganizationFormsSubmitResponse();
  useEffect(() => {
    if (data?.response && !isDirty) {
      reset({ answers: data.response.answers });
    }
  }, [data?.response, isDirty, reset]);
  if (isLoading) {
    return (
      <div aria-label="Loading form" className="space-y-4 p-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }
  if (!data) {
    return <div className="p-6 text-destructive">Unable to load this form.</div>;
  }
  const response = data.response;
  const form = response?.form ?? data.form;
  const begin = async () => {
    try {
      const started = await start.mutateAsync({ organizationId, formId });
      queryClient.setQueryData(getParentOrganizationFormsGetFormQueryKey(organizationId, formId), {
        ...data,
        response: started,
      });
    } catch {
      // The global mutation handler reports errors.
    }
  };
  const persist = async () => {
    if (response) {
      try {
        const saved = await save.mutateAsync({ organizationId, responseId: response.id, data: { answers } });
        queryClient.setQueryData(getParentOrganizationFormsGetFormQueryKey(organizationId, formId), {
          ...data,
          response: saved,
        });
        reset({ answers: saved.answers });
        toast.success("Draft saved");
      } catch {
        // Preserve edits; the global mutation handler reports errors.
      }
    }
  };
  const finalize = async () => {
    if (response) {
      const missing = form.fields.find(
        (field) =>
          field.isRequired &&
          field.type !== OrganizationFormFieldType.Heading &&
          field.type !== OrganizationFormFieldType.Instructions &&
          (field.type === OrganizationFormFieldType.Acknowledgement
            ? answers[field.id] !== "true"
            : !answers[field.id]?.trim()),
      );
      if (missing) {
        setValidationMessage(`${missing.label} is required.`);
        return;
      }
      try {
        const saved = await submit.mutateAsync({ organizationId, responseId: response.id, data: { answers } });
        queryClient.setQueryData(getParentOrganizationFormsGetFormQueryKey(organizationId, formId), {
          ...data,
          response: saved,
        });
        reset({ answers: saved.answers });
        toast.success("Form submitted");
      } catch {
        // Preserve edits; the global mutation handler reports errors.
      }
    }
  };
  const isSubmitted = response?.status === OrganizationFormResponseStatus.Submitted;
  const isPending = save.isPending || submit.isPending;
  const draftSubtitle = response ? "Draft in progress" : "Ready to complete";
  return (
    <div className="flex flex-1 min-h-0 flex-col p-4 md:p-6">
      <UnsavedChangesGuard
        isDirty={!isSubmitted && isDirty}
        description="Your answers have not been saved. Save your draft before leaving to keep these changes."
      />
      <PortalPageHeader
        className="mb-8"
        breadcrumbs={[
          { label: "Forms", to: "/organizations/$organizationId/parent/forms", params: { organizationId } },
          { label: form.name },
        ]}
        title={form.name}
        subtitle={isSubmitted ? "Submitted form" : draftSubtitle}
      />
      <div className={organizationFormContainerClassName}>
        {!response ? (
          <Button onClick={begin} disabled={start.isPending}>
            Start form
          </Button>
        ) : (
          <>
            {!isSubmitted ? (
              <p className="mb-4 text-sm text-muted-foreground">
                Use Save draft to keep your progress. Changes are not saved automatically.
              </p>
            ) : null}
            <div className={organizationFormGridClassName}>
              {form.fields.map((field) => (
                <ResponseField
                  key={field.id}
                  field={field}
                  value={answers[field.id] ?? ""}
                  disabled={isSubmitted || isPending}
                  onChange={(value) => {
                    setValue("answers", { ...answers, [field.id]: value }, { shouldDirty: true });
                    setValidationMessage(undefined);
                  }}
                />
              ))}
            </div>
            {validationMessage ? (
              <p role="alert" className="mt-4 text-sm text-destructive">
                {validationMessage}
              </p>
            ) : null}
            {!isSubmitted ? (
              <div className="mt-6 flex justify-end gap-2 border-t pt-4">
                <Button variant="outline" onClick={persist} disabled={isPending}>
                  <SaveIcon className="mr-2 size-4" />
                  Save draft
                </Button>
                <Button onClick={finalize} disabled={isPending}>
                  <SendIcon className="mr-2 size-4" />
                  Submit form
                </Button>
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}

export const Route = createFileRoute("/_app/organizations/$organizationId/parent/forms_/$formId")({
  params: { parse: (params) => z.object({ formId: numericIdParamSchema }).parse(params) },
  loader: ({ params, ...loaderContext }) =>
    ensureRouteQueryData(
      loaderContext,
      getParentOrganizationFormsGetFormQueryOptions(params.organizationId, params.formId),
    ),
  component: ParentFormRoute,
});

function ParentFormRoute() {
  const { organizationId, formId } = Route.useParams();
  return <ParentFormDetailPage key={`${organizationId}:${formId}`} />;
}
